import { FilterTags } from "@shared/components/CatalogSearch/FilterTags";
import { useCatalogSearch } from "@shared/hooks";

import type { Filter, IngredientFilter, IngredientSelectionParams } from "../CatalogSearch";

import { IngredientFilterTagsWithExclusions } from "./IngredientFilterTagsWithExclusions";

import * as styles from "./SearchFilterTags.styles";

interface Props {
  searchedIngredients: IngredientFilter[];
  onIngredientsTagClose: () => void;
  onClearFilters: () => void;
  onIngredientFilter: (options: IngredientSelectionParams) => void;
}

const SearchFilterTags = ({ onClearFilters }: Props) => {
  const {
    activeFilters,
    isOnlyShowInStock,
    setIsOnlyShowInStock,
    isOnlyShowFavorites,
    setIsOnlyShowFavorites,
    removeActiveFilter,
    activeBrands,
    setActiveBrands,
    activeSupplementTypes,
    setActiveSupplementTypes,
    activeConditions,
    setActiveConditions,
    activeCategories,
    setActiveCategories,
  } = useCatalogSearch();

  const brandTags: Filter[] = activeBrands.map(brand => ({
    name: brand.name,
    category: "Brand",
    id: brand.id,
  }));

  const handleOnRemoveBrands = (filter: Filter) =>
    setActiveBrands(activeBrands.filter(activeBrand => activeBrand.id !== filter.id));

  const supplementTypesTags: Filter[] = activeSupplementTypes.map(supplementType => ({
    name: supplementType.name,
    category: "SupplementType",
    id: supplementType.id,
  }));

  const handleOnRemoveSupplementTypes = (filter: Filter) =>
    setActiveSupplementTypes(
      activeSupplementTypes.filter(activeSupplementType => activeSupplementType.id !== filter.id)
    );

  const handleOnRemoveConditions = (filter: Filter) =>
    setActiveConditions(
      activeConditions.filter(activeCondition => activeCondition.id !== filter.id)
    );

  const inStockTag: Filter[] = isOnlyShowInStock
    ? [
        {
          name: "In Stock",
          category: "InStock",
          id: "InStockId",
        },
      ]
    : [];

  const handleOnRemoveInStockTag = () => setIsOnlyShowInStock(false);

  const favoriteTag: Filter[] = isOnlyShowFavorites
    ? [
        {
          name: "Favorites",
          category: "Favorite",
          id: "FavoriteId",
        },
      ]
    : [];

  const handleOnRemoveFavoriteTag = () => setIsOnlyShowFavorites(false);

  const categoryTags: Filter[] =
    activeCategories?.map(category => ({
      name: category.name,
      category: "Category",
      id: category.id,
    })) ?? [];

  const handleOnRemoveCategory = (filter: Filter) =>
    setActiveCategories(
      activeCategories?.filter(activeCategory => activeCategory.id !== filter.id)
    );

  const conditionsTags: Filter[] =
    activeConditions?.map(condition => ({
      name: condition.name,
      category: "Condition",
      id: condition.id,
    })) ?? [];

  const filterTags = [
    ...inStockTag,
    ...favoriteTag,
    ...activeFilters,
    ...brandTags,
    ...supplementTypesTags,
    ...conditionsTags,
    ...categoryTags,
  ];

  const onRemoveActiveFilter = (filter: Filter) => {
    switch (filter.category) {
      case "Brand":
        handleOnRemoveBrands(filter);
        break;
      case "SupplementType":
        handleOnRemoveSupplementTypes(filter);
        break;
      case "Condition":
        handleOnRemoveConditions(filter);
        break;
      case "Category":
        handleOnRemoveCategory(filter);
        break;
      case "InStock":
        handleOnRemoveInStockTag();
        break;
      case "Favorite":
        handleOnRemoveFavoriteTag();
        break;
      default:
        removeActiveFilter(filter);
        break;
    }
  };

  return (
    <div css={styles.searchesWrapper}>
      <IngredientFilterTagsWithExclusions />
      <FilterTags
        filterTags={filterTags}
        onClearAllFilterTags={onClearFilters}
        onRemoveFilterTag={onRemoveActiveFilter}
      />
    </div>
  );
};

export { SearchFilterTags };
